'use client';

import { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';
import { saveToolScore } from '@/lib/tool-score-storage';

interface HealthCategory {
  id: string;
  name: string;
  icon: string;
  questions: { id: string; question: string; weight: number }[];
}

interface Scores {
  [key: string]: number;
}

const CATEGORIES: HealthCategory[] = [
  {
    id: 'code-quality',
    name: 'Code Quality',
    icon: '✨',
    questions: [
      { id: 'cq1', question: 'Code is readable and well-structured', weight: 2 },
      { id: 'cq2', question: 'Consistent coding standards followed', weight: 1.5 },
      { id: 'cq3', question: 'Low complexity (cyclomatic, cognitive)', weight: 2 },
      { id: 'cq4', question: 'Minimal code duplication', weight: 1.5 },
      { id: 'cq5', question: 'Proper error handling throughout', weight: 2 },
    ],
  },
  {
    id: 'testing',
    name: 'Testing',
    icon: '🧪',
    questions: [
      { id: 't1', question: 'High test coverage (>80%)', weight: 2 },
      { id: 't2', question: 'Unit tests for critical paths', weight: 2 },
      { id: 't3', question: 'Integration tests exist', weight: 1.5 },
      { id: 't4', question: 'Tests are maintainable', weight: 1 },
      { id: 't5', question: 'Tests run fast (<5 min)', weight: 1 },
    ],
  },
  {
    id: 'documentation',
    name: 'Documentation',
    icon: '📚',
    questions: [
      { id: 'd1', question: 'README is comprehensive', weight: 1.5 },
      { id: 'd2', question: 'API documentation exists', weight: 2 },
      { id: 'd3', question: 'Architecture documented', weight: 2 },
      { id: 'd4', question: 'Code comments where needed', weight: 1 },
      { id: 'd5', question: 'Onboarding docs available', weight: 1.5 },
    ],
  },
  {
    id: 'architecture',
    name: 'Architecture',
    icon: '🏗️',
    questions: [
      { id: 'a1', question: 'Clear separation of concerns', weight: 2 },
      { id: 'a2', question: 'Scalable design patterns', weight: 2 },
      { id: 'a3', question: 'Loose coupling, high cohesion', weight: 2 },
      { id: 'a4', question: 'Database schema well-designed', weight: 1.5 },
      { id: 'a5', question: 'Security by design', weight: 2 },
    ],
  },
  {
    id: 'dependencies',
    name: 'Dependencies',
    icon: '📦',
    questions: [
      { id: 'dep1', question: 'Dependencies are up to date', weight: 2 },
      { id: 'dep2', question: 'No known vulnerabilities', weight: 2.5 },
      { id: 'dep3', question: 'Minimal dependency count', weight: 1 },
      { id: 'dep4', question: 'Dependencies well-maintained', weight: 1.5 },
      { id: 'dep5', question: 'License compliance checked', weight: 1 },
    ],
  },
  {
    id: 'performance',
    name: 'Performance',
    icon: '⚡',
    questions: [
      { id: 'p1', question: 'Response times acceptable', weight: 2 },
      { id: 'p2', question: 'Database queries optimized', weight: 2 },
      { id: 'p3', question: 'Caching implemented', weight: 1.5 },
      { id: 'p4', question: 'No memory leaks', weight: 2 },
      { id: 'p5', question: 'Performance monitoring exists', weight: 1.5 },
    ],
  },
  {
    id: 'deployment',
    name: 'Deployment',
    icon: '🚀',
    questions: [
      { id: 'deploy1', question: 'CI/CD pipeline exists', weight: 2 },
      { id: 'deploy2', question: 'Automated deployments', weight: 2 },
      { id: 'deploy3', question: 'Rollback capability', weight: 2 },
      { id: 'deploy4', question: 'Environment parity', weight: 1.5 },
      { id: 'deploy5', question: 'Infrastructure as code', weight: 1.5 },
    ],
  },
  {
    id: 'monitoring',
    name: 'Monitoring',
    icon: '📊',
    questions: [
      { id: 'm1', question: 'Error tracking implemented', weight: 2 },
      { id: 'm2', question: 'Performance monitoring', weight: 1.5 },
      { id: 'm3', question: 'Logging is comprehensive', weight: 1.5 },
      { id: 'm4', question: 'Alerts configured', weight: 2 },
      { id: 'm5', question: 'Dashboards for key metrics', weight: 1 },
    ],
  },
];

export default function CodebaseHealthAnalyzerPage() {
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'codebase-health-analyzer',
    toolCategory: 'analyzer',
  });

  const categories = CATEGORIES;

  const [scores, setScores] = useState<Scores>(() => {
    const initial: Scores = {};
    categories.forEach(cat => {
      cat.questions.forEach(q => {
        initial[q.id] = 3;
      });
    });
    return initial;
  });

  const updateScore = (questionId: string, score: number) => {
    setScores(prev => ({ ...prev, [questionId]: score }));
  };

  const categoryScores = useMemo(() => {
    return categories.map(category => {
      const totalWeight = category.questions.reduce((sum, q) => sum + q.weight, 0);
      const weightedScore = category.questions.reduce((sum, q) => {
        return sum + (scores[q.id] * q.weight);
      }, 0);
      const percentage = (weightedScore / (totalWeight * 5)) * 100;

      let grade: string;
      let color: string;
      if (percentage >= 90) { grade = 'A'; color = 'text-green-500'; }
      else if (percentage >= 80) { grade = 'B'; color = 'text-blue-400'; }
      else if (percentage >= 70) { grade = 'C'; color = 'text-yellow-500'; }
      else if (percentage >= 60) { grade = 'D'; color = 'text-accent'; }
      else { grade = 'F'; color = 'text-red-500'; }

      return {
        ...category,
        score: percentage,
        grade,
        color,
      };
    });
  }, [scores, categories]);

  const overallScore = useMemo(() => {
    const avg = categoryScores.reduce((sum, cat) => sum + cat.score, 0) / categoryScores.length;
    let grade: string;
    if (avg >= 90) grade = 'A';
    else if (avg >= 80) grade = 'B';
    else if (avg >= 70) grade = 'C';
    else if (avg >= 60) grade = 'D';
    else grade = 'F';

    return { score: avg, grade };
  }, [categoryScores]);

  useEffect(() => {
    saveToolScore('codebase-health-analyzer', overallScore.score);
  }, [overallScore.score]);

  const recommendations = useMemo(() => {
    const recs: string[] = [];
    const sorted = [...categoryScores].sort((a, b) => a.score - b.score);
    sorted.forEach(cat => {
      if (cat.score < 70) {
        recs.push(`Improve ${cat.name}: Currently at ${cat.score.toFixed(0)}% (Grade ${cat.grade})`);
      }
    });
    return recs.slice(0, 5);
  }, [categoryScores]);

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const gaps = categoryScores
      .filter(cat => cat.score < 70)
      .map(cat => ({
        id: cat.id,
        title: `${cat.name} needs improvement`,
        severity: cat.score < 50 ? 'critical' as const :
                  cat.score < 60 ? 'high' as const : 'medium' as const,
        description: `Current score: ${cat.score.toFixed(0)}% (Grade ${cat.grade})`,
      }));

    const recs = categoryScores
      .filter(cat => cat.score < 90)
      .sort((a, b) => a.score - b.score)
      .slice(0, 5)
      .map(cat => ({
        id: cat.id,
        title: `Improve ${cat.name}`,
        priority: cat.score < 60 ? 'high' as const :
                  cat.score < 75 ? 'medium' as const : 'low' as const,
        description: `Target: Grade B (80%+). Current: ${cat.score.toFixed(0)}%`,
      }));

    const scoresByCategory: Record<string, number> = {};
    categoryScores.forEach(cat => {
      scoresByCategory[cat.id] = cat.score;
    });

    const assessment = await saveResult({
      name: `Codebase Health Assessment - ${new Date().toLocaleDateString()}`,
      score_overall: overallScore.score,
      scores: scoresByCategory,
      answers: scores,
      gaps,
      recommendations: recs,
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">🏥</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              Codebase Health Analyzer
            </h1>
          </div>
          <p className="text-xl text-foreground-secondary">
            Self-assess your codebase health across 8 critical dimensions
          </p>
        </div>

        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">Home</Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Codebase Health Analyzer</span>
        </nav>

        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this analyzer</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Rate each statement from 1 (strongly disagree) to 5 (strongly agree)</li>
            <li>Review your scores by category and overall health grade</li>
            <li>Follow the prioritized recommendations</li>
            <li>Re-assess periodically to track improvements</li>
          </ol>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {categories.map(category => (
              <div key={category.id} className="bg-surface rounded-lg border border-border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{category.icon}</span>
                  <h3 className="text-xl font-bold text-foreground">{category.name}</h3>
                </div>

                <div className="space-y-4">
                  {category.questions.map(question => (
                    <div key={question.id} className="border-b border-border-muted pb-4 last:border-0">
                      <div className="mb-2">
                        <div className="text-sm text-foreground/90 mb-2">{question.question}</div>
                        <div className="flex items-center gap-2">
                          {[1, 2, 3, 4, 5].map(value => (
                            <button
                              key={value}
                              onClick={() => updateScore(question.id, value)}
                              className={`w-10 h-10 rounded font-semibold ${
                                scores[question.id] === value
                                  ? 'bg-accent text-accent-foreground'
                                  : 'bg-background border border-border text-foreground'
                              } hover:bg-accent/80 transition-colors`}
                            >
                              {value}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            <div className="bg-surface rounded-lg border border-border p-6 sticky top-4">
              <h3 className="text-xl font-bold text-foreground mb-4">Overall Health</h3>
              <div className="text-center py-6 mb-4">
                <div className="text-6xl font-bold text-accent mb-2">
                  {overallScore.grade}
                </div>
                <div className="text-2xl text-foreground-secondary">
                  {overallScore.score.toFixed(0)}%
                </div>
              </div>

              <div className="space-y-3">
                {categoryScores.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span>{cat.icon}</span>
                      <span className="text-sm text-foreground-secondary">{cat.name}</span>
                    </div>
                    <span className={`font-bold ${cat.color}`}>
                      {cat.grade} ({cat.score.toFixed(0)}%)
                    </span>
                  </div>
                ))}
              </div>

              {recommendations.length > 0 && (
                <div className="mt-6 pt-6 border-t border-border-muted">
                  <h4 className="font-bold text-accent mb-3">Top Priorities</h4>
                  <ul className="space-y-2">
                    {recommendations.map((rec, i) => (
                      <li key={i} className="text-xs text-foreground-secondary">• {rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Save to Command Center */}
              {commandCenterEnabled && !saved && (
                <div className="mt-6 pt-6 border-t border-border-muted">
                  <button
                    onClick={handleSaveToCommandCenter}
                    disabled={saving}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save to Command Center'}
                  </button>
                </div>
              )}
              {saved && (
                <div className="mt-6 pt-6 border-t border-border-muted">
                  <div className="px-4 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Saved to Command Center
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
